import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddressBook,
  ChartBar,
  CloudSlash,
  Factory,
  GearSix,
  House,
  List,
  Plus,
  Question,
  SignOut,
  Tray,
  UserCircle,
  UsersThree,
  type Icon,
} from '@phosphor-icons/react';
import { ROLE_LABEL, initials, type SessionUser } from '@helpdesk/shared';
import { getJson } from '../api/client.ts';
import { forgetMe, machineLookupQuery, meQuery } from '../api/queries.ts';
import { OUTBOX_MAX_AGE_HOURS } from '@helpdesk/shared';
import { beginLogout, takeEntraLogout } from '../lib/logout.ts';
import { hostOutbox, ownPendingCount } from '../lib/outbox.ts';
import { syncPush, unsubscribePush } from '../lib/push.ts';
import { forgetName } from '../lib/reporter-name.ts';
import { setAppBadge } from '../pwa.ts';
import { AppPrompts } from './app-prompts.tsx';
import { OutboxSentBand } from './outbox-agent.tsx';
import { OutboxDeviceNotice, OutboxPanel } from './outbox-panel.tsx';

/** Ohne die Sitzung dieser Person gehen ihre Meldungen nicht raus: Das soll sie vor dem Abmelden wissen. */
export async function confirmLogout(userId: string): Promise<boolean> {
  const count = await ownPendingCount(userId);
  if (!count) return true;
  const left = count === 1 ? '1 Meldung bleibt' : `${count} Meldungen bleiben`;
  return window.confirm(`${left} ungesendet auf diesem Gerät. Gesendet wird frühestens, wenn Sie sich hier wieder anmelden. Nach ${OUTBOX_MAX_AGE_HOURS} Stunden werden Text und Fotos gelöscht. Trotzdem abmelden?`);
}

export async function logout(userId: string): Promise<void> {
  if (await confirmLogout(userId)) await endSession();
}

/**
 * Abmelden ist zweistufig (§9.5): Sitzung hier beenden UND bei Entra.
 * Vorher das Push-Abo dieses Geräts lösen – sonst zeigt ein geteiltes Tablet
 * weiter die Benachrichtigungen der vorherigen Person.
 * Antwortet der Server nicht, holt `/abgemeldet` das Beenden nach.
 */
async function leave(): Promise<void> {
  await Promise.race([unsubscribePush().catch(() => undefined), new Promise((r) => setTimeout(r, 2500))]);
  // Zuerst, denn die Maschinenliste belegt `localStorage`, und der Merker darf nicht am vollen Speicher scheitern.
  forgetMe();
  forgetName();
  await beginLogout();
  navigator.serviceWorker?.controller?.postMessage({ type: 'logout' });
  setAppBadge(0);
  window.location.href = takeEntraLogout() ?? '/abgemeldet';
}

let leaving: Promise<void> | null = null;

/** Läuft einmal je Seite und wird nie zurückgesetzt: Am Ende steht immer ein Seitenwechsel. */
export function endSession(): Promise<void> {
  leaving ??= leave();
  return leaving;
}

/** Netzstatus des Geräts; bei Rückkehr lädt die App alle Daten frisch. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const qc = useQueryClient();
  useEffect(() => {
    const on = () => {
      setOnline(true);
      void qc.invalidateQueries();
    };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [qc]);
  return online;
}

/**
 * Hallen-Tablet: Nach der Leerlauffrist der Sitzung automatisch abmelden, auch bei
 * Entra (§9.5). Sonst landet die nächste Person ohne Passwort im Konto des Vorgängers.
 */
function useDeviceIdle(idleMinutes: number | null): number | null {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!idleMinutes) return;
    const limit = idleMinutes * 60_000;
    // Bei kurzer Frist stünde die Warnung sonst von Anfang an da.
    const warnFrom = Math.min(2 * 60_000, limit / 2);
    let last = Date.now();
    const touch = () => {
      last = Date.now();
    };
    // `scroll` steigt nicht auf: Scrollbereiche in der Seite sieht nur die Capture-Phase.
    const options = { capture: true, passive: true };
    const events = ['pointerdown', 'keydown', 'scroll'];
    events.forEach((e) => window.addEventListener(e, touch, options));
    const timer = setInterval(() => {
      const remaining = limit - (Date.now() - last);
      if (remaining <= 0) {
        clearInterval(timer);
        void endSession();
      }
      setLeft(remaining <= warnFrom ? Math.max(0, Math.ceil(remaining / 1000)) : null);
    }, 1000);
    return () => {
      clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, touch, options));
    };
  }, [idleMinutes]);
  return left;
}

type Counts = { review: number; open: number; unassigned: number; mine: number } | null;
type NavItem = { to: string; label: string; icon: Icon; badge?: number | undefined };

function navItems(user: SessionUser, counts: Counts): NavItem[] {
  const worker = user.role !== 'reporter';
  const items: NavItem[] = [
    { to: '/', label: 'Start', icon: House },
    { to: '/melden', label: 'Störung melden', icon: Plus },
    { to: '/tickets', label: worker ? 'Tickets' : 'Meine Meldungen', icon: Tray, badge: worker ? counts?.unassigned : undefined },
    { to: '/maschinen', label: 'Maschinen', icon: Factory },
  ];
  if (worker) items.push({ to: '/teams', label: 'Teams', icon: UsersThree });
  if (worker) items.push({ to: '/kontakte', label: 'Kontakte', icon: AddressBook });
  if (user.role === 'lead') items.push({ to: '/auswertung', label: 'Auswertung', icon: ChartBar });
  if (user.isSysadmin) items.push({ to: '/admin', label: 'Verwaltung', icon: GearSix });
  items.push({ to: '/profil', label: 'Profil', icon: UserCircle });
  items.push({ to: '/hilfe', label: 'Hilfe', icon: Question });
  return items;
}

const isActive = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

export function AppShell({ user, children }: { user: SessionUser; children: ReactNode }) {
  const [drawer, setDrawer] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(hostOutbox, []);
  const idleLeft = useDeviceIdle(user.deviceIdleMinutes);
  const me = useQuery(meQuery);
  const online = useOnline() && !me.data?.offline;
  const vapidKey = me.data?.vapidPublicKey ?? null;
  // Auf jeder Seite, nicht nur dort, wo der Push-Hinweis steht. Die Rückkehr des Netzes holt einen gescheiterten Abgleich nach.
  useEffect(() => {
    if (online) void syncPush(user, vapidKey);
  }, [user, vapidKey, online]);
  // Hier und nicht erst auf `/melden`: Wer in die WLAN-Lücke kommt, hat die Liste dann schon. Gestartet ohne Netz, holt sie die Rückkehr nach.
  useQuery(machineLookupQuery);
  const worker = user.role !== 'reporter';
  const counts = useQuery({
    queryKey: ['tickets', 'counts'],
    queryFn: () => getJson<{ counts: Counts }>('/api/tickets', { tab: 'open', pageSize: 1 }),
    enabled: worker,
    refetchInterval: 60_000,
  });
  const qc = useQueryClient();
  useEffect(() => setDrawer(false), [pathname]);

  // Zahl am App-Symbol: nicht zugewiesene Tickets (Instandhaltung). Geteilte Tablets zeigen nichts.
  const unassigned = counts.data?.counts?.unassigned ?? 0;
  useEffect(() => {
    setAppBadge(worker && !user.deviceId ? unassigned : 0);
  }, [worker, unassigned, user.deviceId]);

  const items = navItems(user, counts.data?.counts ?? null);

  const navList = (onDark: boolean) =>
    items.map((it) => {
      const active = isActive(pathname, it.to);
      const IconCmp = it.icon;
      return (
        <Link
          key={it.to}
          to={it.to}
          aria-current={active ? 'page' : undefined}
          className={`flex min-h-12 items-center gap-3 border-l-4 px-4 text-[16px] no-underline transition-colors ${
            active
              ? 'border-arnold-red bg-steel-800 font-semibold text-white'
              : `border-transparent font-medium ${onDark ? 'text-steel-300 hover:bg-steel-900 hover:text-white' : ''}`
          }`}
        >
          <IconCmp size={22} weight={active ? 'fill' : 'regular'} className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">{it.label}</span>
          {it.badge ? (
            <span className="min-w-7 bg-arnold-red px-1.5 text-center font-mono text-[13px] leading-6 text-white" aria-label={`${it.badge} nicht zugewiesen`}>
              {it.badge}
            </span>
          ) : null}
        </Link>
      );
    });

  const logoutButton = (
    <button
      type="button"
      className="flex min-h-12 w-full cursor-pointer items-center gap-3 px-5 text-[16px] font-medium text-steel-300 hover:bg-steel-900 hover:text-white"
      onClick={() => void logout(user.id)}
    >
      <SignOut size={22} className="shrink-0" />
      Abmelden
    </button>
  );

  return (
    <div className="flex min-h-[100dvh] flex-col bg-steel-50">
      <header className="sticky top-0 z-[200] bg-steel-900 pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-3 pr-[max(1rem,env(safe-area-inset-right))] pl-[max(1rem,env(safe-area-inset-left))]">
          <Link to="/" className="flex items-center text-white" aria-label="Startseite">
            <img src="/arnold-logo-white.svg" alt="Arnold AG" className="h-[24px]" />
          </Link>
          <span className="hidden border-l border-steel-700 pl-3 font-display text-[15px] font-semibold tracking-[0.05em] whitespace-nowrap text-steel-300 uppercase sm:inline">
            Instandhaltung
          </span>
          <div className="ml-auto flex min-w-0 items-center gap-2.5">
            <OutboxPanel userId={user.id} />
            <span className="hidden border border-steel-700 px-2.5 py-1 text-[14px] font-medium text-steel-300 lg:inline">
              {ROLE_LABEL[user.role]}
              {user.isSysadmin ? ' · Verwaltung' : ''}
            </span>
            {user.deviceId ? (
              <button type="button" onClick={() => void logout(user.id)} className="min-h-10 cursor-pointer bg-arnold-red px-4 font-display text-[15px] font-bold tracking-[0.05em] text-white uppercase">
                Abmelden
              </button>
            ) : null}
            <Link
              to="/profil"
              className="flex h-9 w-9 shrink-0 items-center justify-center bg-arnold-red font-display text-[14px] font-bold text-white no-underline"
              title={`Profil von ${user.displayName}`}
              aria-label="Profil"
            >
              {initials(user.displayName)}
            </Link>
          </div>
        </div>
      </header>

      {!online ? (
        <div className="flex items-center justify-center gap-2 bg-warn px-4 py-2 text-center text-[15px] font-semibold text-white" role="status">
          <CloudSlash size={20} weight="bold" className="shrink-0" />
          Kein Netz. Meldungen werden auf diesem Gerät gespeichert und gesendet, sobald die App wieder Verbindung hat. Erst die Ticketnummer bestätigt den Eingang.
        </div>
      ) : null}

      <OutboxSentBand links />

      {idleLeft !== null ? (
        <div className="bg-warn px-4 py-2 text-center text-[15px] font-semibold text-white" role="status">
          Automatische Abmeldung in {idleLeft} s. Zum Weiterarbeiten auf den Bildschirm tippen.
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 items-stretch">
        <nav className="hidden w-[232px] shrink-0 flex-col bg-steel-950 py-3 md:flex" aria-label="Hauptnavigation">
          {navList(true)}
          <div className="mt-auto pt-3">{logoutButton}</div>
        </nav>
        <main className="min-w-0 flex-1">
          <div className="mx-auto max-w-[1280px] px-[max(1rem,env(safe-area-inset-left))] pt-5 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:px-6 md:px-8 md:pt-8 md:pb-12">
            {pathname === '/' || pathname === '/tickets' ? <AppPrompts user={user} vapidPublicKey={vapidKey} /> : null}
            {children}
          </div>
        </main>
      </div>

      {/* Handy: Leiste unten mit den häufigsten Zielen und dem roten Meldeknopf in der Mitte. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-[150] border-t border-steel-800 bg-steel-950 pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="Schnellnavigation"
      >
        <div className="grid grid-cols-5">
          <TabLink to="/" label="Start" icon={House} active={pathname === '/'} />
          <TabLink to="/tickets" label={worker ? 'Tickets' : 'Meldungen'} icon={Tray} active={pathname.startsWith('/tickets')} badge={worker ? unassigned : 0} />
          <Link
            to="/melden"
            onClick={() => void qc.invalidateQueries({ queryKey: ['machines'] })}
            className="-mt-4 flex flex-col items-center justify-end gap-0.5 pb-1.5 no-underline"
            aria-current={pathname.startsWith('/melden') ? 'page' : undefined}
          >
            <span className="flex h-14 w-14 items-center justify-center bg-arnold-red text-white shadow-[var(--shadow-raised)]">
              <Plus size={30} weight="bold" />
            </span>
            <span className="text-[12px] font-semibold text-white">Melden</span>
          </Link>
          <TabLink to="/maschinen" label="Maschinen" icon={Factory} active={pathname.startsWith('/maschinen')} />
          <button
            type="button"
            onClick={() => setDrawer(true)}
            aria-expanded={drawer}
            className={`flex min-h-16 cursor-pointer flex-col items-center justify-center gap-0.5 ${drawer ? 'text-white' : 'text-steel-300'}`}
          >
            <List size={26} />
            <span className="text-[12px] font-medium">Mehr</span>
          </button>
        </div>
      </nav>

      {drawer ? (
        <div className="fixed inset-0 z-[300] bg-[rgba(20,25,29,0.6)] md:hidden" onClick={() => setDrawer(false)} role="presentation">
          <nav
            className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-auto bg-steel-950 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
            aria-label="Alle Bereiche"
          >
            <div className="mx-auto mb-2 h-1 w-10 bg-steel-700" aria-hidden />
            <div className="px-5 pt-1 pb-3 text-[14px] text-steel-400">
              {user.displayName} · {ROLE_LABEL[user.role]}
            </div>
            {navList(true)}
            <div className="mt-1 border-t border-steel-800 pt-1">{logoutButton}</div>
          </nav>
        </div>
      ) : null}
    </div>
  );
}

function TabLink({ to, label, icon: IconCmp, active, badge = 0 }: { to: string; label: string; icon: Icon; active: boolean; badge?: number }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={`relative flex min-h-16 flex-col items-center justify-center gap-0.5 no-underline ${active ? 'text-white' : 'text-steel-300'}`}
    >
      {active ? <span className="absolute inset-x-4 top-0 h-[3px] bg-arnold-red" aria-hidden /> : null}
      <span className="relative">
        <IconCmp size={26} weight={active ? 'fill' : 'regular'} />
        {badge > 0 ? (
          <span className="absolute -top-1.5 -right-3 min-w-5 bg-arnold-red px-1 text-center font-mono text-[11px] leading-[18px] text-white">{badge > 99 ? '99+' : badge}</span>
        ) : null}
      </span>
      <span className={`text-[12px] ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>
    </Link>
  );
}

/** Schlanker Rahmen für Seiten ohne Anmeldung (QR, Bestätigung, Hilfe). */
export function PublicShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-steel-50">
      <header className="bg-steel-900 pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center gap-3 px-[max(1rem,env(safe-area-inset-left))]">
          <a href="/" className="flex items-center">
            <img src="/arnold-logo-white.svg" alt="Arnold AG" className="h-[24px]" />
          </a>
          <span className="border-l border-steel-700 pl-3 font-display text-[15px] font-semibold tracking-[0.05em] text-steel-300 uppercase">
            Instandhaltung
          </span>
        </div>
      </header>
      <main className={`mx-auto w-full ${wide ? 'max-w-[1280px]' : 'max-w-[640px]'} flex-1 px-4 pt-5 pb-16`}>
        <OutboxDeviceNotice kind="quick" />
        {children}
      </main>
      <footer className="no-print border-t border-[var(--border-default)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-center text-[14px] text-[var(--text-muted)]">
        <a href="/datenschutz" className="text-[var(--text-muted)] underline underline-offset-2">
          Datenschutz
        </a>
        <span className="mx-2" aria-hidden>
          ·
        </span>
        <a href="/hilfe" className="text-[var(--text-muted)] underline underline-offset-2">
          Hilfe
        </a>
      </footer>
    </div>
  );
}
